import { useEffect, useRef, useState } from 'react';
import {
    AssistantMessage,
    ToolTypeEnum,
} from '../../../shared/types/assistant';
import { postQuestionUseCase } from '../../../services/assistant/post-question.use-case';
import { useNavigate, useParams } from 'react-router-dom';
import { PAGENOTFOUND } from '../../../routes/paths';
import {
    asyncDownloadFile,
    asyncGetChatAssistant,
    asyncGetLastTenChats,
    asyncValidateChatAssistantLink,
} from '../../../services/chat-assistant.service';
import { ChatAssistantLogs } from '../../../shared/types/chat-assistant-logs';
import { getPreviousMessageUseCase } from '../../../services/assistant/get-message.use-case';
import { ToastSwal } from '../../components/general/toastSwals/toastSwal';
import { Attachment, UserTypeEnum } from '../../../shared/types/shared';
import { ChatAssistant } from '../../../shared/types/chat-assistant';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';

export const useAssistantPage = () => {
    const navigate = useNavigate();
    const { idChatAssistant } = useParams();
    const { logUser } = useSelector((state: RootState) => state.general);
    const { type } = logUser || {};
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [messages, setMessages] = useState<AssistantMessage[]>([]);
    const [threadId, setThreadId] = useState<string>();
    const [previousResponseId, setPreviousResponseId] = useState('');
    const [lastChats, setLastChats] = useState<ChatAssistantLogs[] | undefined>(
        []
    );
    const [isOpenHistory, setIsOpenHistory] = useState(false);
    const [openOptionButton, setOpenOptionButton] = useState(false);
    const anchorRef = useRef<HTMLDivElement>(null);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [isVisibleButtons, setIsVisibleButtons] = useState(false);
    const [assistant, setAssistant] = useState<ChatAssistant>();
    const [isAssistantAdvancedMode, setAssistantAdvancedMode] = useState(false);
    const [displayName, setDisplayName] = useState('Nova');

    const options = [
        {
            label: 'Modo básico',
            tooltip:
                'Consulta sin carga de archivos. Ideal para preguntas rápidas basadas en la base de conocimiento.',
        },
        {
            label: 'Modo procesamiento',
            tooltip:
                'Permite subir archivos, procesar datos, ejecutar código y generar gráficos o archivos de salida.',
        },
    ];

    const handleHistoryClose = () => {
        setIsOpenHistory((prev) => !prev);
    };

    const handleMenuItemClick = (index: number) => {
        setSelectedIndex(index);
        setOpenOptionButton(false);
    };

    const handleToggle = () => {
        setOpenOptionButton((prevOpen) => !prevOpen);
    };

    const handleClose = (event: Event) => {
        if (
            anchorRef.current &&
            anchorRef.current.contains(event.target as HTMLElement)
        )
            return;
        setOpenOptionButton(false);
    };

    const onClickDownload = async (contentItem: any) => {
        const result = await asyncDownloadFile(
            idChatAssistant,
            contentItem.id as string,
            threadId as string
        );
        if (result) {
            const { url, filename } = result;
            const link = document.createElement('a');
            link.href = url;
            link.download = filename || 'archivo.pdf';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        }
    };

    useEffect(() => {
        asyncValidateChatAssistantLink(idChatAssistant).then((response) => {
            if (!response?.id) navigate(PAGENOTFOUND);
            if (response?.displayName) setDisplayName(response.displayName);
        });
        asyncGetLastTenChats(idChatAssistant).then((response) => {
            setLastChats(response.items);
        });
    }, []);

    const handlePost = async (text: string, files: Attachment[]) => {
        const toolType =
            selectedIndex === 0
                ? ToolTypeEnum.FileSearch
                : ToolTypeEnum.CodeInterpreter;
        setIsLoading(true);

        const tempId =
            'temp-' +
            Date.now().toString(36) +
            Math.random().toString(36).slice(2, 8);
        const attachedFilesText = files.length
            ? `\n\nArchivos adjuntos:\n${files
                .map((f) => `• ${f.name}`)
                .join('\n')}`
            : '';

        setMessages((prev) => [
            ...prev,
            {
                id: tempId,
                isGpt: false,
                content: [
                    {
                        type: 'text',
                        text: { value: `${text}${attachedFilesText}` },
                    },
                ],
            },
        ]);

        const response = await postQuestionUseCase(
            idChatAssistant,
            threadId || '',
            text,
            toolType,
            files,
            previousResponseId
        );
        setIsLoading(false);

        const assistantMsgs: AssistantMessage[] = (response?.message || []).map(
            (m) => {
                const content: AssistantMessage['content'] = [];

                (m.content || []).forEach((c) => {
                    if (c.type === 'text' && c.text?.value) {
                        content.push({
                            type: 'text',
                            text: { value: c.text.value },
                            fileOutputs: c.fileOutputs ?? [],
                        });
                    }
                });

                return { id: m.id, isGpt: m.role === 'assistant', content };
            }
        );

        setMessages((prev) => {
            const withoutTemp = prev.filter((m) => m.id !== tempId);
            const existingIds = new Set(withoutTemp.map((m) => m.id));
            const unique = assistantMsgs.filter((m) => !existingIds.has(m.id));
            return [...withoutTemp, ...unique];
        });

        setThreadId(response?.threadId || threadId);
        setPreviousResponseId(response?.responseId || '');
    };

    const handleSelectConversation = async (threadId: string) => {
        setIsLoading(true);
        setThreadId(threadId);
        setAssistantAdvancedMode(false);

        const replies = await getPreviousMessageUseCase(threadId);
        setIsLoading(false);

        if (
            !Array.isArray(replies) &&
            (replies as any)?.message === 'SAM_ASSISTANT-ERROR-FIND'
        ) {
            ToastSwal.fire({
                text: 'Este chat ya no está disponible, se iniciará uno nuevo automáticamente.',
                icon: 'error',
                timer: 5000,
                position: 'center-end',
            }).then(() => window.location.reload());
            return;
        }

        const msgs: AssistantMessage[] = (replies ?? []).map((reply) => ({
            id: reply.id,
            isGpt: reply.role === 'assistant',
            content: (reply.content ?? []).map((c: any) => ({
                type: c.type,
                text: { value: c.text?.value ?? '' },
                fileOutputs: c.fileOutputs ?? [],
            })),
        }));
        setMessages(msgs);
        if (replies?.some((item) => item.containerId?.trim())) {
            setAssistantAdvancedMode(true);
        }
    };

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        if (threadId)
            asyncGetLastTenChats(idChatAssistant).then((response) =>
                setLastChats(response.items)
            );
    }, [messages]);

    useEffect(() => {
        const getAssistant = async () => {
            const assistant = await asyncGetChatAssistant(idChatAssistant);
            setAssistant(assistant);
        };
        getAssistant();
        if (!idChatAssistant?.trim()) navigate(PAGENOTFOUND);
    }, [idChatAssistant]);

    useEffect(() => {
        const shouldShow = Boolean(assistant?.isAdvancedMode) && !threadId;
        const isAllowedRole =
            type === UserTypeEnum.TenderManager ||
            type === UserTypeEnum.Admin ||
            type === UserTypeEnum.SuperAdmin;
        setIsVisibleButtons(shouldShow && isAllowedRole);
    }, [assistant?.interpreterAssistantId, threadId, type]);

    return {
        messages,
        handlePost,
        isLoading,
        messagesEndRef,
        lastChats,
        handleSelectConversation,
        threadId,
        isOpenHistory,
        handleHistoryClose,
        anchorRef,
        options,
        selectedIndex,
        openOptionButton,
        handleClose,
        handleMenuItemClick,
        handleToggle,
        isVisibleButtons,
        onClickDownload,
        isAssistantAdvancedMode,
        displayName,
    };
};
